/**
 * HUB-107 §5 — the user's own MCP connections, one card per enabled server the
 * user may use. How a card connects follows the server's auth type:
 *   oauth        → Connect (a full-page navigation into the hub's consent flow)
 *   user_key     → paste a personal key (write-only, stored encrypted)
 *   hub_identity / shared_key → nothing to do ("automatic")
 * The view is pure/controlled so the no-jsdom test renders it statically.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { McpUserServer } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { hub, hubBaseUrl } from '@/lib/hub-client';

export const MCP_CONNECTIONS_QUERY_KEY = ['mcp-connections'] as const;

/** The OAuth consent entry — a browser redirect chain, NOT an API call. */
export function mcpConnectStartUrl(serverId: string): string {
  return `${hubBaseUrl}/mcp-connections/${serverId}/start`;
}

export const MCP_STATUS_COPY: Record<McpUserServer['status'], string> = {
  connected: 'Connected',
  not_connected: 'Not connected',
  credential_invalid: 'Connection expired — connect again',
  automatic: 'Connected automatically',
};

export const MCP_CALLBACK_ERROR_COPY: Record<string, string> = {
  oauth_denied: 'Access was denied, so nothing was connected.',
  oauth_unavailable: 'This server cannot be connected right now. Ask an admin to check its OAuth settings.',
  exchange_failed: 'The service did not return a usable credential. Try connecting again.',
  server_unavailable: 'This server is no longer available.',
};

export interface McpServerCardProps {
  server: McpUserServer;
  keyInput: string;
  busy: boolean;
  error: string | null;
  onKeyInput: (value: string) => void;
  onSaveKey: () => void;
  onConnect: () => void;
  onDisconnect: () => void;
}

export function McpServerCard(props: McpServerCardProps) {
  const { server, keyInput, busy, error, onKeyInput, onSaveKey, onConnect, onDisconnect } = props;
  const connected = server.status === 'connected';
  return (
    <Card data-mcp-server={server.slug}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{server.displayName}</CardTitle>
          <span className={connected || server.status === 'automatic'
            ? 'text-xs font-medium text-emerald-600'
            : 'text-xs text-muted-foreground'}
          >
            {MCP_STATUS_COPY[server.status]}
          </span>
        </div>
        <CardDescription>
          {server.status === 'automatic'
            ? 'The copilot uses this server with your hub identity. Nothing to connect.'
            : 'The copilot uses this server with YOUR account only. It asks you before any change.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {server.authType === 'user_key' && (
          <div className="flex gap-2">
            <Input
              type="password"
              autoComplete="off"
              aria-label={`Personal key for ${server.displayName}`}
              placeholder={connected ? 'Enter a new key to replace the stored one' : 'Paste your personal key'}
              value={keyInput}
              onChange={(e) => onKeyInput(e.target.value)}
            />
            <Button type="button" onClick={onSaveKey} disabled={busy || keyInput.trim() === ''}>
              Save key
            </Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 empty:hidden">
          {server.authType === 'oauth' && (
            <Button type="button" onClick={onConnect} disabled={busy}>
              {connected ? 'Reconnect' : 'Connect'}
            </Button>
          )}
          {(connected || server.status === 'credential_invalid') && (
            <Button type="button" variant="outline" onClick={onDisconnect} disabled={busy}>
              Disconnect
            </Button>
          )}
        </div>
        {error && <Alert variant="destructive">{error}</Alert>}
      </CardContent>
    </Card>
  );
}

export function McpConnectionsSection() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: MCP_CONNECTIONS_QUERY_KEY, queryFn: () => hub.mcpConnectionsList() });
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const servers = query.data?.servers ?? [];
  if (servers.length === 0) return null;

  const act = async (serverId: string, action: () => Promise<unknown>) => {
    setBusyId(serverId);
    setErrors((prev) => ({ ...prev, [serverId]: null }));
    try {
      await action();
      setKeys((prev) => ({ ...prev, [serverId]: '' }));
      await queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_QUERY_KEY });
    } catch (err) {
      setErrors((prev) => ({ ...prev, [serverId]: err instanceof Error ? err.message : 'Request failed' }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Copilot tools (MCP servers)</h2>
      {servers.map((server) => (
        <McpServerCard
          key={server.id}
          server={server}
          keyInput={keys[server.id] ?? ''}
          busy={busyId === server.id}
          error={errors[server.id] ?? null}
          onKeyInput={(value) => setKeys((prev) => ({ ...prev, [server.id]: value }))}
          onSaveKey={() => void act(server.id, () =>
            hub.mcpConnectionSetKey({ params: { serverId: server.id }, body: { key: keys[server.id] ?? '' } }))}
          onConnect={() => { window.location.href = mcpConnectStartUrl(server.id); }}
          onDisconnect={() => void act(server.id, () => hub.mcpConnectionDelete({ params: { serverId: server.id } }))}
        />
      ))}
    </section>
  );
}
